import { fetchApi } from "@/lib/api/index.ts";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface Memory {
  id: string;
  memory: string;
  created_at: string;
  updated_at: string;
  type: "short_term" | "long_term";
}

export const getTeenMemories = async (userId: string) => {
  const response = await fetchApi<{ memories: Memory[] }>({
    url: `/v2/users/${userId}/memories`,
    method: "GET",
  });

  // Sort memories by updated_at in descending order (most recent first)
  return response.data.memories.sort(
    (a, b) =>
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
  );
};

export const useTeenMemories = (userId: string) => {
  return useQuery<Memory[], Error>({
    queryKey: ["teen-memories", userId],
    queryFn: () => getTeenMemories(userId),
  });
};

export interface EditMemoryInput {
  userId: string;
  memoryId: string;
  memory: string;
  type?: "short_term" | "long_term";
  metadata?: Record<string, unknown>;
}

export const useEditMemory = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    Memory,
    Error,
    EditMemoryInput,
    { previousMemories: Memory[] | undefined }
  >({
    mutationKey: ["edit-memory"],
    mutationFn: async (data) => {
      const response = await fetchApi<{ memory: Memory }>({
        url: `/v2/users/${data.userId}/memories/${data.memoryId}`,
        method: "PATCH",
        data: {
          memory: data.memory,
          type: data.type,
          metadata: data.metadata,
        },
      });
      return response.data.memory;
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ["teen-memories", userId] });
      const previousMemories = queryClient.getQueryData<Memory[]>([
        "teen-memories",
        userId,
      ]);
      queryClient.setQueryData<Memory[]>(["teen-memories", userId], (old) =>
        (old || []).map((m) =>
          m.id === data.memoryId
            ? {
                ...m,
                memory: data.memory,
                type: data.type || m.type,
                updated_at: new Date().toISOString(),
              }
            : m,
        ),
      );
      return { previousMemories };
    },
    onError: (_err, _data, context) => {
      if (context?.previousMemories) {
        queryClient.setQueryData(
          ["teen-memories", userId],
          context.previousMemories,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["teen-memories", userId] });
    },
  });
};

export interface DeleteMemoryInput {
  userId: string;
  memoryId: string;
}

export const useDeleteMemory = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    DeleteMemoryInput,
    Error,
    DeleteMemoryInput,
    { previousMemories: Memory[] | undefined }
  >({
    mutationKey: ["delete-memory"],
    mutationFn: async (data) => {
      await fetchApi({
        url: `/v2/users/${data.userId}/memories/${data.memoryId}`,
        method: "DELETE",
      });
      return data;
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ["teen-memories", userId] });
      const previousMemories = queryClient.getQueryData<Memory[]>([
        "teen-memories",
        userId,
      ]);
      queryClient.setQueryData<Memory[]>(["teen-memories", userId], (old) =>
        (old || []).filter((m) => m.id !== data.memoryId),
      );
      return { previousMemories };
    },
    onError: (_err, _data, context) => {
      if (context?.previousMemories) {
        queryClient.setQueryData(
          ["teen-memories", userId],
          context.previousMemories,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["teen-memories", userId] });
    },
  });
};
